import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { RoutesMap } from "../../../routes";
import { EJWTStatus, validateJWT } from "../../../utils";
import { isNetworkError, queryClient, tokenRefreshMutationFn } from "../../../api";
import { usePersistStore } from "../../../store/persistStore";
import { useLoginFormStore } from "../../../store/loginFormStore";
import { isNewAuthorization } from "../../../store/authorizationStore";

const REFRESH_CHECK_INTERVAL_MS = 30000;
const REFRESH_FAILURE_COOLDOWN_MS = 60000;

/**
 * Yes this is component. NO, it can't be written elsewhere.
 * It's a component because it needs to be rendered in the tree to have access to hooks.
 * Keep it in the root of the application.
 *
 * This component is responsible for refreshing JWT token when it's about to expire.
 */
export function TokenRefresher() {
  const navigate = useNavigate();
  const deviceToken = usePersistStore(state => state.deviceJWT);
  const jwtToken = usePersistStore(state => state.jwt);
  const token = deviceToken ?? jwtToken;
  const clearJwtToken = usePersistStore(state => state.clearJwt);
  const clearDeviceToken = usePersistStore(state => state.clearDeviceJwt);
  const setJWT = usePersistStore(state => state.setJwt);
  const setDeviceJWT = usePersistStore(state => state.setDeviceJwt);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFailureAtRef = useRef<number>(0);
  const inFlightRef = useRef<Promise<unknown> | null>(null);

  const refreshTokenMutation = useMutation({
    mutationKey: ["tokenRefresh"],
    mutationFn: tokenRefreshMutationFn,
    onSuccess: (data) => {
      if (isNewAuthorization()) {
        return;
      }

      if (usePersistStore.getState().deviceJWT) {
        setDeviceJWT(data.token);
      } else {
        setJWT(data.token);
      }
      // JWT changed: refetch everything that might've failed with 401 while idle.
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (isNewAuthorization()) {
        return;
      }

      if (isNetworkError(e) && e.internalCode !== 10000) {
        // This is an API error, and it's not 500.
        // For some reason token can't be refreshed. Logout.
        const loginFormStore = useLoginFormStore.getState();
        clearJwtToken();
        clearDeviceToken();
        loginFormStore.clearPhoneForm();
        loginFormStore.clearSMSForm();
        loginFormStore.clearForm();
        navigate(RoutesMap.login.index);
        return;
      }
      // For transient errors (incl "500-ish" internal 10000), cooldown to avoid spamming.
      lastFailureAtRef.current = Date.now();
    },
    retry: false,
  });

  const requestRefresh = useCallback(() => {
    if (refreshTokenMutation.isPending) {
      return;
    }

    // single-flight (even if multiple queries/components request refresh)
    if (inFlightRef.current) {
      return;
    }

    // cooldown after a failed attempt (prevents burst loops)
    if (Date.now() - lastFailureAtRef.current < REFRESH_FAILURE_COOLDOWN_MS) {
      return;
    }

    inFlightRef.current = refreshTokenMutation.mutateAsync()
      .finally(() => {
        inFlightRef.current = null;
      });
  }, [refreshTokenMutation.isPending, refreshTokenMutation.mutateAsync]);

  // Check token status immediately when token changes
  useEffect(() => {
    if (!token) {
      return;
    }

    switch (validateJWT(token)) {
      case EJWTStatus.Expired:
      case EJWTStatus.RefreshRequired: {
        requestRefresh();
        break;
      }
    }
  }, [token, requestRefresh]);

  // Set up periodic check every 30 seconds to catch tokens that expire while idle
  useEffect(() => {
    if (!token) {
      return;
    }

    const checkAndRefresh = () => {
      // Get current token from store to ensure we have the latest value
      const currentState = usePersistStore.getState();
      const currentToken = currentState.deviceJWT ?? currentState.jwt;
      if (!currentToken) {
        return;
      }

      switch (validateJWT(currentToken)) {
        case EJWTStatus.Valid:
          break;
        case EJWTStatus.Expired:
        case EJWTStatus.RefreshRequired: {
          requestRefresh();
          break;
        }
      }
    };

    // Check immediately
    checkAndRefresh();

    // Then check every 30 seconds
    intervalRef.current = setInterval(checkAndRefresh, REFRESH_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [token, requestRefresh]);

  return null;
}
